/**
 * iter-121.47 — Gallery pagination footer.
 *
 * Matches the screenshot: "Showing 1 to 8 of 128 assets" on the left,
 * page-number buttons on the right with prev/next chevrons. Handles
 * collapsed middle ("…") for long lists.
 */
import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

function buildPageList(current, total) {
    // Always show 1, current-1, current, current+1, total. Insert "…"
    // wherever there's a gap. Caps the visible page numbers at ~7 chips
    // so the row never overflows on a narrow viewport.
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const set = new Set([1, total, current - 1, current, current + 1]);
    if (current <= 3) [2, 3, 4].forEach((p) => set.add(p));
    if (current >= total - 2) [total - 1, total - 2, total - 3].forEach((p) => set.add(p));
    const pages = [...set].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < pages.length; i += 1) {
        out.push(pages[i]);
        if (i + 1 < pages.length && pages[i + 1] - pages[i] > 1) {
            out.push(`gap-${pages[i]}`);
        }
    }
    return out;
}

export default function GalleryPagination({ page, pageSize, totalCount, onPageChange }) {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const firstShown = totalCount === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const lastShown = Math.min(safePage * pageSize, totalCount);
    const pageList = buildPageList(safePage, totalPages);

    const go = (p) => {
        if (typeof p !== "number") return;
        const clamped = Math.min(Math.max(1, p), totalPages);
        if (clamped !== safePage) onPageChange(clamped);
    };

    return (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-testid="gallery-pagination">
            <div className="text-xs text-muted-foreground" data-testid="gallery-pagination-summary">
                Showing <span className="font-medium text-foreground">{firstShown}</span> to{" "}
                <span className="font-medium text-foreground">{lastShown}</span> of{" "}
                <span className="font-medium text-foreground">{totalCount}</span> asset{totalCount === 1 ? "" : "s"}
            </div>

            <div className="inline-flex items-center gap-1">
                <button
                    type="button"
                    onClick={() => go(safePage - 1)}
                    disabled={safePage <= 1}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="gallery-page-prev"
                    aria-label="Previous page"
                >
                    <ChevronLeft className="h-4 w-4" />
                </button>

                {pageList.map((p) => (
                    typeof p === "number" ? (
                        <button
                            key={p}
                            type="button"
                            onClick={() => go(p)}
                            aria-current={p === safePage ? "page" : undefined}
                            className={`flex h-8 min-w-[2rem] items-center justify-center rounded-md border px-2 text-xs font-medium shadow-sm transition-colors ${
                                p === safePage
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "border-border bg-card text-foreground hover:bg-secondary/40"
                            }`}
                            data-testid={`gallery-page-${p}`}
                        >
                            {p}
                        </button>
                    ) : (
                        <span key={p} className="px-1 text-xs text-muted-foreground select-none" aria-hidden="true">…</span>
                    )
                ))}

                <button
                    type="button"
                    onClick={() => go(safePage + 1)}
                    disabled={safePage >= totalPages}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="gallery-page-next"
                    aria-label="Next page"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
